// QR codes drawn as SVG paths (crisp at any size), and the links space icons encode.
import QRCode from 'qrcode'

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/**
 * A space's short code: the last 40 bits of its id (the random part of a UUIDv7; the
 * start is a timestamp) as 8 Crockford base-32 characters. Derived, never stored: a
 * member's device resolves it by checking its own spaces.
 */
export function spaceCode(spaceId: string): string {
  const hex = spaceId.replace(/-/g, '').slice(-10)
  let n = BigInt(`0x${hex || '0'}`)
  let out = ''
  for (let i = 0; i < 8; i++) {
    out = CROCKFORD[Number(n & 31n)] + out
    n >>= 5n
  }
  return out
}

/** The space among `spaces` a code (or, from older links, a full id) refers to. */
export function spaceForCode<T extends { id: string }>(spaces: readonly T[], code: string): T | undefined {
  const c = code.toUpperCase()
  return spaces.find((s) => s.id.toUpperCase() === c || spaceCode(s.id) === c)
}

/**
 * A space's link, in QR alphanumeric form (upper case: a smaller code than lower case):
 * `HTTPS://HOST/S/<CODE>`. Opening it on a member's device opens the space.
 */
export function spaceUrl(spaceId: string, origin = globalThis.location?.origin ?? ''): string {
  return `${origin}/S/${spaceCode(spaceId)}`.toUpperCase()
}

export interface QrMatrix {
  size: number
  dark: boolean[]
}

const cache = new Map<string, QrMatrix>()

/** Modules of a QR code for `text` (low error correction: smallest code, fine on screens). */
export function qrMatrix(text: string): QrMatrix {
  let m = cache.get(text)
  if (!m) {
    const q = QRCode.create(text, { errorCorrectionLevel: 'L' })
    m = { size: q.modules.size, dark: Array.from(q.modules.data, Boolean) }
    cache.set(text, m)
  }
  return m
}

/** One SVG path for the dark modules, a rectangle per horizontal run. */
export function qrPath(m: QrMatrix): string {
  let d = ''
  for (let y = 0; y < m.size; y++) {
    let x = 0
    while (x < m.size) {
      if (!m.dark[y * m.size + x]) {
        x++
        continue
      }
      const start = x
      while (x < m.size && m.dark[y * m.size + x]) x++
      d += `M${start} ${y}h${x - start}v1h${start - x}z`
    }
  }
  return d
}
